import { useEffect, useRef } from "react";

// Director public mic — "Director mic → On Air" (LSC-633, ADR 0247). The counterpart
// to the PRIVATE talkback return path (ADR 0213): a latched control that lets the
// Director's voice join the FAN Program mix (and the recording / replay) deliberately,
// WITHOUT occupying a video slot or requiring a TAKE — for introductions, questions, or
// live contribution. Private talkback is unchanged and stays separate (ADR 0213).
//
// Routing model (web + LiveKit only; no wire/route/schema change — like talkback.tsx
// and talentAudio.tsx):
//   • The Director publishes a SECOND, dedicated mic track named `director-onair`
//     (distinct from the private `talkback` track). It is kept MUTED until the Director
//     latches On Air, and the console opens subscription to ALL participants only while
//     it is on air (the SFU-permission state machine lives in DirectorConsole, co-
//     ordinated with talkback so the two planes never conflict — see ADR 0247).
//   • Every surface that should hear the public Director — the FAN watch (ConsumerWatch,
//     and the headless program-renderer that feeds HLS + VOD, so the recording captures
//     it by construction) and the TALENT surfaces (Legend Go-Live + each Guest join) —
//     runs `useDirectorOnAirAudio` to attach the `director-onair` track into a hidden
//     <audio>. Audibility is the track's MUTE state + the SFU permission, exactly the
//     way talent/talkback audio works; nothing here decides policy.
//   • It is matched by the reserved TRACK NAME, so it is cleanly distinct from the
//     private talkback track (which `isTalkbackPublication` identifies) and from talent
//     mics. `isTalentAudio` (ADR 0218) excludes it so the talent-conversation bus never
//     double-plays it.
//
// This module holds the PURE, unit-tested pieces (the reserved name + the predicate)
// plus the attach hook. The real audible output is beta QA (jsdom can't run WebRTC
// audio); the hook's attach/detach/volume bookkeeping is exercised against a test fake.

/** The published track name that marks the Director's PUBLIC on-air mic (how fans and
 *  talent tell it apart from a program feed, a talent mic, or the private talkback). */
export const DIRECTOR_ONAIR_TRACK = "director-onair";

/** True when a remote publication is the Director's public on-air mic — matched by the
 *  reserved track name. Tolerant of the shapes tests/LiveKit expose. */
export function isDirectorOnAirPublication(
  pub: { trackName?: string; name?: string } | undefined | null,
): boolean {
  if (!pub) return false;
  return pub.trackName === DIRECTOR_ONAIR_TRACK || pub.name === DIRECTOR_ONAIR_TRACK;
}

// Structural shapes — just enough of LiveKit's Room/track surface for the hook and a
// test fake, kept tolerant of the fields LiveKit actually exposes.
interface MicTrack {
  kind?: string; // "audio" | "video"
  sid?: string;
  attach?: (el?: HTMLMediaElement) => HTMLMediaElement | void;
  detach?: (el?: HTMLMediaElement) => void;
}
interface MicPublication {
  trackName?: string;
  name?: string;
  kind?: string; // "audio" | "video" on the publication
  trackSid?: string;
  sid?: string;
  track?: MicTrack | null;
  isSubscribed?: boolean;
  setSubscribed?: (subscribed: boolean) => void;
}
interface MicParticipant {
  trackPublications?: Map<string, MicPublication> | MicPublication[];
  audioTrackPublications?: Map<string, MicPublication> | MicPublication[];
}
interface MicRoom {
  on(event: string, cb: (...args: unknown[]) => void): unknown;
  off?(event: string, cb: (...args: unknown[]) => void): unknown;
  remoteParticipants?: Map<string, MicParticipant> | MicParticipant[];
}

function iterValues<T>(coll: Map<string, T> | T[] | undefined): T[] {
  if (!coll) return [];
  if (coll instanceof Map) return Array.from(coll.values());
  if (Array.isArray(coll)) return coll;
  return [];
}

function pubKey(pub: MicPublication, track?: MicTrack | null): string {
  return pub.trackSid ?? pub.sid ?? track?.sid ?? pub.trackName ?? pub.name ?? "";
}

/** Options applied to every attached Director on-air element. For the FAN this carries
 *  the fan's OWN volume + mute (ADR 0064) so the fan's speaker control still governs it
 *  — the public Director mic is its own plane, independent of the production slate/hold
 *  muting that gates the program source (ADR 0235), honouring only the fan's own mute.
 *  Talent surfaces pass nothing (full volume, unmuted). */
export interface DirectorOnAirOpts {
  volume?: number;
  muted?: boolean;
}

/** Attach the Director's PUBLIC on-air mic (`director-onair`) into a hidden container so
 *  this surface hears the Director when they are On Air (LSC-633). Force-subscribes the
 *  publication if auto-subscribe lagged (the ADR 0075 pattern), re-applies volume/mute on
 *  change, and detaches cleanly on unsubscribe / teardown (no orphaned audio, no echo).
 *  The private talkback track (ADR 0213) is NOT matched here — it has its own listener. */
export function useDirectorOnAirAudio(
  room: MicRoom | null,
  opts?: DirectorOnAirOpts,
): { containerRef: React.RefObject<HTMLDivElement | null> } {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const els = useRef<Map<string, { el: HTMLMediaElement; track: MicTrack }>>(new Map());
  const volume = opts?.volume ?? 1;
  const muted = opts?.muted ?? false;

  // Re-assert the fan's own volume/mute on every attached element whenever it changes
  // (a re-attach can reset element.muted on iOS — same hazard as ConsumerWatch, LSC-330).
  useEffect(() => {
    for (const { el } of els.current.values()) {
      try {
        el.muted = muted;
        el.volume = volume;
      } catch {
        /* best-effort */
      }
    }
  }, [volume, muted]);

  useEffect(() => {
    if (!room) return;
    const play = (track: MicTrack | undefined | null, pub: MicPublication | undefined) => {
      if (!track || !pub || !isDirectorOnAirPublication(pub) || !track.attach) return;
      const k = pubKey(pub, track);
      if (!k || els.current.has(k)) return; // idempotent — never double-attach
      try {
        const el = track.attach(); // no arg → LiveKit creates + plays a fresh <audio>
        if (el) {
          el.autoplay = true;
          el.muted = muted;
          el.volume = volume;
          containerRef.current?.appendChild(el);
          els.current.set(k, { el, track });
        }
      } catch {
        // jsdom / detached element — audio is cosmetic under test
      }
    };
    const stop = (track: MicTrack | undefined | null, pub: MicPublication | undefined) => {
      if (!pub) return;
      const k = pubKey(pub, track ?? undefined);
      const entry = els.current.get(k);
      if (!entry) return;
      try {
        entry.track.detach?.(entry.el);
      } catch {
        /* best-effort */
      }
      try {
        entry.el.remove();
      } catch {
        /* best-effort */
      }
      els.current.delete(k);
    };

    // Scan the room for a `director-onair` publication: attach it if the track is already
    // here, else force-subscribe so a lagging auto-subscribe still resolves (ADR 0075).
    const scan = () => {
      for (const p of iterValues<MicParticipant>(room.remoteParticipants)) {
        const pubs = iterValues<MicPublication>(p.audioTrackPublications ?? p.trackPublications);
        for (const pub of pubs) {
          if (!isDirectorOnAirPublication(pub)) continue;
          if (pub.track) play(pub.track, pub);
          else if (!(pub.isSubscribed ?? false)) {
            try {
              pub.setSubscribed?.(true);
            } catch {
              /* best-effort */
            }
          }
        }
      }
    };

    const onSub = (...args: unknown[]) => play(args[0] as MicTrack, args[1] as MicPublication);
    const onUnsub = (...args: unknown[]) => stop(args[0] as MicTrack, args[1] as MicPublication);
    const onPublished = () => scan();

    scan();
    room.on("trackSubscribed", onSub);
    room.on("trackUnsubscribed", onUnsub);
    room.on("trackPublished", onPublished);
    const bookkeeping = els.current;
    return () => {
      room.off?.("trackSubscribed", onSub);
      room.off?.("trackUnsubscribed", onUnsub);
      room.off?.("trackPublished", onPublished);
      for (const { el, track } of bookkeeping.values()) {
        try {
          track.detach?.(el);
        } catch {
          /* best-effort */
        }
        try {
          el.remove();
        } catch {
          /* best-effort */
        }
      }
      bookkeeping.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  return { containerRef };
}
